import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Spin, message } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { fetchMySchedule, plannedRange, statusColor, textOn, taskLabel } from './scheduleUtils';

const LABEL_COL = 'minmax(160px, 240px)';

// Gantt-style month view: one row per task, planned range as a bar, due date as a flag.
const MyScheduleTimeline = ({ reloadKey, filters, onOpenTask, onUndatedCount }) => {
  const [month, setMonth] = useState(() => dayjs().startOf('month'));
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const days = useMemo(() => {
    const n = month.daysInMonth();
    return Array.from({ length: n }, (_, i) => month.add(i, 'day'));
  }, [month]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMySchedule(month, month.endOf('month'), filters);
      setTasks(data.tasks || []);
      onUndatedCount && onUndatedCount(data.undated_count || 0, data);
    } catch {
      message.error('Failed to load your schedule');
    } finally {
      setLoading(false);
    }
  }, [month, filters, onUndatedCount]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const today = dayjs().startOf('day');
  const last = days.length - 1;
  const gridCols = `${LABEL_COL} repeat(${days.length}, minmax(26px, 1fr))`;
  const dayIndex = (d) => d.startOf('day').diff(month, 'day');

  const rows = useMemo(() => [...tasks].sort((a, b) => {
    const ka = (plannedRange(a)?.[0] || dayjs(a.due_date)).valueOf();
    const kb = (plannedRange(b)?.[0] || dayjs(b.due_date)).valueOf();
    return ka - kb;
  }), [tasks]);

  return (
    <div className="ms-timeline">
      <div className="ms-timeline__nav">
        <button type="button" className="ms-btn" onClick={() => setMonth((m) => m.subtract(1, 'month'))} aria-label="Previous month"><LeftOutlined /></button>
        <button type="button" className="ms-btn" onClick={() => setMonth((m) => m.add(1, 'month'))} aria-label="Next month"><RightOutlined /></button>
        <button type="button" className="ms-btn" onClick={() => setMonth(dayjs().startOf('month'))}>Today</button>
        <span className="ms-timeline__title">{month.format('MMMM YYYY')}</span>
        {loading && <Spin size="small" />}
      </div>

      <div className="ms-timeline__scroll">
        <div className="ms-timeline__grid" style={{ minWidth: 160 + days.length * 26 }}>
          {/* Day header */}
          <div className="ms-tl-row ms-tl-row--head" style={{ gridTemplateColumns: gridCols }}>
            <div className="ms-tl-label">Task</div>
            {days.map((d, i) => (
              <div
                key={i}
                className={`ms-tl-day${[0, 6].includes(d.day()) ? ' is-weekend' : ''}${d.isSame(today, 'day') ? ' is-today' : ''}`}
              >
                <span>{d.format('dd').charAt(0)}</span>
                <strong>{d.date()}</strong>
              </div>
            ))}
          </div>

          {rows.length === 0 && !loading && (
            <div className="ms-timeline__empty">No planned or due tasks this month.</div>
          )}

          {rows.map((t) => {
            const range = plannedRange(t);
            const color = statusColor(t);
            let bar = null;
            if (range) {
              const s = dayIndex(range[0]);
              const e = dayIndex(range[1]);
              if (e >= 0 && s <= last) {
                const cs = Math.max(0, s);
                const ce = Math.min(last, e);
                bar = (
                  <div
                    className={`ms-tl-bar${t.is_completed ? ' is-done' : ''}${t.is_overdue ? ' is-overdue' : ''}${s < 0 ? ' cut-left' : ''}${e > last ? ' cut-right' : ''}`}
                    style={{ gridColumn: `${cs + 2} / ${ce + 3}`, background: color, color: textOn(color) }}
                    title={`Planned: ${t.estimated_start_date || '—'} → ${t.estimated_end_date || '—'}`}
                  >
                    {t.status_name}
                  </div>
                );
              }
            }
            let flag = null;
            if (t.due_date) {
              const di = dayIndex(dayjs(t.due_date));
              if (di >= 0 && di <= last) {
                flag = (
                  <div
                    className={`ms-tl-flag${t.is_completed ? ' is-done' : ''}`}
                    style={{ gridColumn: `${di + 2} / ${di + 3}` }}
                    title={`Due: ${t.due_date}${t.is_overdue ? ' (overdue)' : ''}`}
                  >
                    🚩
                  </div>
                );
              }
            }
            return (
              <div
                key={t.id}
                className="ms-tl-row ms-tl-row--task"
                style={{ gridTemplateColumns: gridCols }}
                onClick={() => onOpenTask && onOpenTask(t.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') onOpenTask && onOpenTask(t.id); }}
              >
                <div className="ms-tl-label" title={`${taskLabel(t)}${t.project_name ? ` — ${t.project_name}` : ''}`}>
                  {t.task_code && <span className="ms-tl-code">{t.task_code}</span>}
                  <span className="ms-tl-title">{t.title}</span>
                </div>
                {/* Background cells keep weekend/today shading aligned with the header */}
                {days.map((d, i) => (
                  <div
                    key={i}
                    className={`ms-tl-cell${[0, 6].includes(d.day()) ? ' is-weekend' : ''}${d.isSame(today, 'day') ? ' is-today' : ''}`}
                    style={{ gridColumn: `${i + 2} / ${i + 3}` }}
                  />
                ))}
                {bar}
                {flag}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MyScheduleTimeline;
